import React, { useState, useEffect } from 'react';
import { Users, UserPlus, SignOut, ShieldCheck, Shield, CaretDown, CaretUp, ChartBar } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { authService } from '../../../services/auth.service';
import { UsageDetailPanel } from './UsageDetailPanel';

export const UserManagementView = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [expandedUser, setExpandedUser] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('user');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const [usersData, usageData] = await Promise.all([
        authService.listUsers(),
        authService.getUsageSummary().catch(() => ({ users: [] }))
      ]);
      
      const rawUsage = usageData.users || [];

      const usageMap = new Map(rawUsage.map((u: any) => [u.email, u.total_tokens]));
      
      const mergedUsers = usersData.users.map((u: any) => ({
        ...u,
        total_tokens: usageMap.get(u.email) || 0
      }));
      
      setUsers(mergedUsers);
    } catch (err: any) {
      toast.error('Failed to load users');
    }
  };

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsLoading(true);
    try {
      await authService.addUser(email, role, name);
      toast.success(`User ${email} added successfully`);
      setEmail('');
      setName('');
      setRole('user');
      fetchUsers();
    } catch (err: any) {
      toast.error(err.message || 'Failed to add user');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForceLogout = async (userEmail: string) => {
    if (!window.confirm(`Force log out ${userEmail}?`)) return;
    
    try {
      await authService.forceLogoutUser(userEmail);
      toast.success(`${userEmail} status set to pending (logged out).`);
      fetchUsers();
    } catch (err: any) {
      toast.error(err.message || 'Failed to force log out');
    }
  };

  const formatNumber = (num: number) => new Intl.NumberFormat('en-US').format(num);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-black text-slate-900 font-outfit flex items-center gap-2">
          <Users size={28} className="text-accent" weight="fill" />
          User Management
        </h1>
      </div>

      {/* Navigation Cards Section */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        
        <button 
          onClick={() => navigate('/admin/analytics')}
          className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex items-center gap-4 hover:border-accent hover:shadow-md transition-all text-left w-full group"
        >
          <div className="p-3 bg-sky-50 text-sky-600 rounded-xl group-hover:bg-accent group-hover:text-white transition-colors">
            <ChartBar size={24} weight="bold" />
          </div>
          <div>
            <div className="text-sm font-bold text-slate-900 group-hover:text-accent transition-colors">Platform Analytics</div>
            <div className="text-xs font-medium text-slate-500 mt-1">View platform-wide token usage and feature adoption.</div>
          </div>
        </button>

      </div>

      {/* Add New User */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
        <h2 className="text-sm font-bold text-slate-800 mb-4 flex items-center gap-2">
          <UserPlus size={18} className="text-accent" />
          Add New User
        </h2>
        
        <form onSubmit={handleAddUser} className="flex flex-col sm:flex-row gap-4 items-end">
          <div className="flex-1 w-full">
            <label className="block text-xs font-semibold text-slate-500 mb-1">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 focus:border-accent focus:ring-4 focus:ring-accent/10 rounded-xl px-4 py-2.5 text-sm transition-smooth outline-none"
              placeholder="Full Name"
              required
            />
          </div>
          
          <div className="flex-1 w-full">
            <label className="block text-xs font-semibold text-slate-500 mb-1">Email Address</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 focus:border-accent focus:ring-4 focus:ring-accent/10 rounded-xl px-4 py-2.5 text-sm transition-smooth outline-none"
              placeholder="user@acr.iitm.ac.in"
              required
            />
          </div>
          
          <div className="w-full sm:w-48">
            <label className="block text-xs font-semibold text-slate-500 mb-1">Role</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 focus:border-accent focus:ring-4 focus:ring-accent/10 rounded-xl px-4 py-2.5 text-sm transition-smooth outline-none"
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          
          <button
            type="submit"
            disabled={isLoading || !email}
            className="w-full sm:w-auto px-6 py-2.5 bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white font-bold rounded-xl text-sm transition-smooth"
          >
            {isLoading ? 'Adding...' : 'Add User'}
          </button>
        </form>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100">
                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Email / User</th>
                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Role</th>
                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Token Usage</th>
                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {users.map((user, idx) => (
                <React.Fragment key={idx}>
                  <tr className={`hover:bg-slate-50/50 transition-colors ${expandedUser === user.email ? 'bg-slate-50/80' : ''}`}>
                    <td className="px-6 py-4">
                      <div className="text-sm font-semibold text-slate-900">{user.email}</div>
                      <div className="text-xs text-slate-400">{user.name || 'Unknown'}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${
                        user.role === 'admin' ? 'bg-indigo-50 text-indigo-700' : 'bg-slate-100 text-slate-700'
                      }`}>
                        {user.role === 'admin' ? <ShieldCheck size={14} weight="fill" /> : <Shield size={14} />}
                        {user.role}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm font-bold text-slate-800">
                        {formatNumber(user.total_tokens || 0)} tokens
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                        user.status === 'approved' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
                      }`}>
                        {user.status || 'unknown'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setExpandedUser(expandedUser === user.email ? null : user.email)}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${
                            expandedUser === user.email 
                              ? 'bg-orange-50 text-orange-700 border border-orange-200/60' 
                              : 'text-slate-600 hover:bg-slate-100'
                          }`}
                        >
                          Token Usage
                          {expandedUser === user.email ? <CaretUp weight="bold" /> : <CaretDown weight="bold" />}
                        </button>
                        <button
                          onClick={() => handleForceLogout(user.email)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-rose-600 hover:bg-rose-50 transition-colors"
                          title="Force Logout"
                        >
                          <SignOut size={14} weight="bold" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expandedUser === user.email && (
                    <tr key={`expand-${idx}`} className="bg-slate-50 border-t border-slate-100">
                      <td colSpan={5} className="p-0">
                        <UsageDetailPanel email={user.email} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-sm text-slate-500 font-medium">
                    No users found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
